/**
 * Qué impuesto lleva cada línea, y cuánto es.
 *
 * Vive suelto y sin importar nada, como el resto de `lib/`. Nació dentro de
 * `devolucion-manual.js` y se sacó aquí el 19-sep-2026 cuando la factura
 * normal necesitó lo mismo: un producto con impuesto propio (comisiones
 * exentas, al 0%) que manda sobre el del cliente.
 *
 * TODO ESTO ES VISTA PREVIA. Quien decide es el servidor
 * (`regimen_fiscal.regimen_de` + `recalcular_totales_factura`), y esto tiene
 * que decir exactamente lo mismo, redondeos incluidos: si la pantalla
 * enseñara una cifra y el papel otra, nadie volvería a fiarse de la pantalla.
 */

const TIPO_GENERAL = 21;
const NO_SUJETO = 'NO_SUJETO';

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * El tipo y el régimen que le tocan a un cliente: { tipo, regimen }.
 *
 * Manda LO GUARDADO en la ficha. El código postal no se mira: deducir el
 * territorio al emitir haría que corregir un CP cambiara el impuesto sin que
 * nadie lo decida.
 */
export function regimenDelCliente(cliente) {
  const ficha = cliente || {};
  const regimenGuardado = String(ficha.tax_type || '').trim().toUpperCase();
  const regimen = ['IVA', 'NO_SUJETO', 'IGIC', 'IPSI'].includes(regimenGuardado)
    ? regimenGuardado : null;
  const tipo = num(ficha.tax_rate);

  if (regimen === null && tipo === null) return { tipo: TIPO_GENERAL, regimen: 'IVA' };
  // No sujeto es 0 por definición, gane quien gane.
  if (regimen === NO_SUJETO) return { tipo: 0, regimen: NO_SUJETO };
  if (tipo === null) return { tipo: TIPO_GENERAL, regimen: regimen || 'IVA' };
  return { tipo, regimen: regimen || 'IVA' };
}

/**
 * El tipo que va a llevar ESTA línea con ESTE cliente.
 *
 * Por orden, igual que el servidor:
 *   1. Cliente no sujeto → 0, y no se discute. Si la operación no está
 *      sujeta, ninguna línea puede estarlo.
 *   2. El `tax_rate` de la línea, si lo tiene. Viene del producto (comisión
 *      exenta → 0) o lo escribió alguien.
 *   3. El del cliente.
 */
export function tipoDeLinea(linea, cliente) {
  const { tipo, regimen } = regimenDelCliente(cliente);
  if (regimen === NO_SUJETO) return 0;
  const propio = num(linea?.tax_rate);
  return propio === null ? tipo : propio;
}

/**
 * Base, cuota y total de una línea escrita, en positivo. Réplica exacta de
 * `recalcular_totales_factura`, con sus dos redondeos: la base ANTES de la
 * cuota. Sin ese orden, pantalla y servidor se separan por céntimos.
 */
export function importeDeLineaEscrita(linea, tipoDefecto = TIPO_GENERAL, regimen = null) {
  const cantidad = num(linea?.quantity) ?? 0;
  const precio = num(linea?.unit_price) ?? 0;
  const descuento = num(linea?.discount) ?? 0;

  const tipoLinea = num(linea?.tax_rate);
  const tipo = (regimen === NO_SUJETO || tipoLinea === null)
    ? Number(tipoDefecto) : tipoLinea;

  const base = Math.round(cantidad * precio * (1 - descuento / 100) * 100) / 100;
  const cuota = Math.round(base * tipo / 100 * 100) / 100;
  return { base, cuota, total: Math.round((base + cuota) * 100) / 100, tipo };
}

/** Lo mismo, pero dándole el cliente en vez del tipo. Para las pantallas. */
export function importeDeLineaConCliente(linea, cliente) {
  const { tipo, regimen } = regimenDelCliente(cliente);
  return importeDeLineaEscrita(linea, tipo, regimen);
}

/** 21 y no 21.0; 7,5 y no 7.5. Para etiquetas. */
export function formatoTipo(tipo) {
  const n = Number(tipo) || 0;
  return String(Math.round(n * 100) / 100).replace('.', ',');
}
